import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'sonner'
import { Check, Loader2 } from 'lucide-react'
import { Button } from '@orisini/ui/components/button'
import { Card } from '@orisini/ui/components/card'
import { cn } from '@orisini/ui/lib/utils'
import { Avatar } from './Avatar'
import { accountsApi, PLATFORMS, type PlatformAccounts, type ProductCode } from '../lib/accounts'

/** Selang pemeriksaan akun baru selama user login di agent. */
const POLL_MS = 4000

/**
 * Pilih akun media sosial untuk sebuah project: platform, lalu akun.
 *
 * Akun platform SENGAJA tidak dibuat dari web: yang menentukan akun mana yang
 * benar-benar terhubung adalah sesi browser di komputer user, jadi akun lahir
 * dari tombol "Add account" di Orisini Agent (lihat services/accounts.ts di
 * produk). Komponen ini menawarkan akun yang sudah login dan belum dipakai
 * project lain, atau menunggu akun baru muncul selama user login di agent.
 */
export function ConnectAccount({
  busy,
  onConnect,
  onCancel,
}: {
  busy: boolean
  onConnect: (productCode: ProductCode, accountId: string) => void
  onCancel?: () => void
}) {
  const [productCode, setProductCode] = useState<ProductCode | null>(null)
  const [platforms, setPlatforms] = useState<PlatformAccounts[] | null>(null)
  const [limit, setLimit] = useState<{ used: number; max: number } | null>(null)
  const [accountId, setAccountId] = useState<string | null>(null)
  /** Akun yang sudah ada saat platform dipilih — yang lain berarti baru login. */
  const awal = useRef<Set<string> | null>(null)

  const platform = platforms?.find((p) => p.productCode === productCode) ?? null
  const label = PLATFORMS.find((p) => p.productCode === productCode)?.label ?? ''
  const pilihan = useMemo(
    () => (platform?.accounts ?? []).filter((a) => !a.workspaceId && a.username && a.status !== 'unlinked'),
    [platform],
  )
  const terpilih = pilihan.find((a) => a.id === accountId) ?? null

  // Muat, lalu periksa ulang berkala supaya akun yang baru selesai login di
  // agent langsung muncul tanpa user menekan apa pun.
  useEffect(() => {
    if (!productCode) return
    let hidup = true
    const muat = async () => {
      try {
        const r = await accountsApi.platformAccounts()
        if (!hidup) return
        setPlatforms(r.platforms)
        setLimit(r.limit)
        const p = r.platforms.find((x) => x.productCode === productCode)
        const ids = (p?.accounts ?? []).filter((a) => a.username).map((a) => a.id)
        if (awal.current === null) {
          awal.current = new Set(ids)
        } else {
          const baru = p?.accounts.find((a) => a.username && !a.workspaceId && !awal.current!.has(a.id))
          if (baru) {
            awal.current.add(baru.id)
            setAccountId(baru.id)
            toast.success(`@${baru.username} is connected`)
          }
        }
      } catch (err) {
        if (hidup) toast.error(err instanceof Error ? err.message : "Couldn't load accounts")
      }
    }
    void muat()
    const t = setInterval(() => void muat(), POLL_MS)
    return () => {
      hidup = false
      clearInterval(t)
    }
  }, [productCode])

  if (!productCode) {
    return (
      <div className="space-y-4">
        <ul className="grid gap-3 sm:grid-cols-2">
          {PLATFORMS.map((p) => (
            <li key={p.label}>
              <button
                type="button"
                disabled={!p.productCode}
                onClick={() => {
                  setProductCode(p.productCode)
                  setAccountId(null)
                  setPlatforms(null)
                  awal.current = null
                }}
                className="flex w-full flex-col items-start gap-1 rounded-lg border border-border p-4 text-left transition-colors hover:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border"
              >
                <span className="font-medium">{p.label}</span>
                <span className="text-xs text-muted-foreground">{p.hint}</span>
              </button>
            </li>
          ))}
        </ul>
        {onCancel && (
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {platforms === null ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Looking for your {label} accounts…
        </div>
      ) : (
        <>
          {pilihan.length > 0 && (
            <section className="space-y-2">
              <h2 className="text-sm font-semibold">Signed in on Orisini Agent</h2>
              <ul className="space-y-2">
                {pilihan.map((a) => (
                  <li key={a.id}>
                    <button
                      type="button"
                      onClick={() => setAccountId(a.id)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
                        accountId === a.id ? 'border-primary bg-primary/5' : 'border-border hover:border-foreground/40',
                      )}
                      aria-pressed={accountId === a.id}
                    >
                      <Avatar url={a.avatarUrl} name={a.username ?? a.label} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium">@{a.username}</span>
                        {a.label !== `@${a.username}` && (
                          <span className="block truncate text-xs text-muted-foreground">{a.label}</span>
                        )}
                      </span>
                      {accountId === a.id && <Check className="size-4 text-primary" />}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Card className="space-y-3 p-5">
            <h2 className="text-sm font-semibold">
              {pilihan.length > 0 ? 'Or connect another account' : 'Connect with Orisini Agent'}
            </h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                Open Orisini Agent on your computer.{' '}
                {platform?.agentConnected ? (
                  <span className="text-emerald-400">It's online.</span>
                ) : (
                  <>
                    It doesn't look online yet —{' '}
                    <Link to="/download" className="text-foreground underline-offset-4 hover:underline">
                      download it
                    </Link>{' '}
                    if you haven't.
                  </>
                )}
              </li>
              <li>Click <span className="text-foreground">Add account</span> and choose {label}.</li>
              <li>Sign in to {label} in the window that opens.</li>
            </ol>
            <p className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              This page picks up the new account as soon as you've signed in.
            </p>
            {limit && limit.used >= limit.max && (
              <p className="text-xs text-destructive">
                You're using {limit.used} of {limit.max} accounts on your plan.{' '}
                <Link to="/billing" className="underline underline-offset-4">Upgrade</Link> to add more.
              </p>
            )}
            {platform && !platform.available && (
              <p className="text-xs text-destructive">{label} is unavailable right now. Try again in a moment.</p>
            )}
          </Card>
        </>
      )}

      <div className="flex justify-between gap-2">
        <Button variant="outline" onClick={() => setProductCode(null)}>Back</Button>
        <Button disabled={!terpilih || busy} onClick={() => terpilih && onConnect(productCode, terpilih.id)}>
          {busy && <Loader2 className="animate-spin" />}
          Connect {terpilih ? `@${terpilih.username}` : 'account'}
        </Button>
      </div>
    </div>
  )
}
